import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/utils/cn';

/**
 * Debounced search.
 *
 * The field is local and the committed value is debounced, so typing stays instant
 * while the server sees one request per pause rather than one per keystroke. Clearing
 * is immediate — waiting 300ms to see the full list back feels broken.
 */

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  clearLabel?: string;
  debounceMs?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
}

export const SearchInput = ({
  value,
  onChange,
  placeholder,
  clearLabel,
  debounceMs = 300,
  autoFocus,
  disabled,
  className,
  id,
  ...aria
}: SearchInputProps) => {
  const [draft, setDraft] = useState(value);
  const [lastCommitted, setLastCommitted] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // A value changed elsewhere — a cleared filter bar, a restored URL — must show here.
  // Adjusted during render rather than in an effect so the field never paints one
  // frame of stale text.
  if (value !== lastCommitted) {
    setLastCommitted(value);
    setDraft(value);
  }

  useEffect(() => () => clearTimeout(timer.current), []);

  const commit = (next: string, immediate = false) => {
    setDraft(next);
    clearTimeout(timer.current);

    const send = () => {
      setLastCommitted(next);
      onChange(next);
    };

    if (immediate) {
      send();
      return;
    }
    timer.current = setTimeout(send, debounceMs);
  };

  return (
    <Input
      id={id}
      type="text"
      role="searchbox"
      enterKeyHint="search"
      value={draft}
      autoFocus={autoFocus}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(event) => commit(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') commit(draft, true);
        if (event.key === 'Escape') commit('', true);
      }}
      prefix={<Search aria-hidden className="h-4 w-4" />}
      suffix={
        draft ? (
          <button
            type="button"
            aria-label={clearLabel}
            onClick={() => commit('', true)}
            className="rounded p-0.5 hover:text-fg"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : undefined
      }
      className={cn('max-w-sm', className)}
      {...aria}
    />
  );
};
